import type { CSS } from '@stitches/react';

import { Box } from '@/components/shared/layout/box';
import { Image } from '@/components/shared/media/image';
import { generateTabletAndMobileImageSources } from '@/components/shared/media/image/generate-sources';
import { Content } from '@/components/shared/my24/content';
import { keyframes, styled } from '@/stitches.config';
import { IItem, IMy24Theme } from '@/types/shared';

const slide = keyframes({
  '20%': { opacity: 1 },
  '50%': { opacity: 1 },
  '70%': { opacity: 0 },
});

const CardImage = styled(Box, {
  aspectRatio: '3 / 2',
  border: '2px solid transparent',
  '@md': {
    borderWidth: '3px',
  },
  '@lg': {
    transform: 'skewX(-21deg)',
    transformOrigin: 'bottom',
    pe: 'all',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -2,
    top: -2,
    width: '75%',
    height: 'calc(100% + 4px)',
    background:
      'radial-gradient(circle, rgba(255, 255, 255, 1) 60%, rgba(255, 255, 255, 0) 100%)',
    '@md': {
      left: -3,
      top: -3,
      height: 'calc(100% + 6px)',
    },
  },
});

const CardImageInner = styled(Box, {
  '@lg': {
    height: '320px !important',
  },
  '& > *': {
    height: 'calc(100% + 2px)',
    '@lg': {
      transform: 'skewX(21deg)',
      transformOrigin: 'bottom',
      width: 'calc(605px + 2px) !important',
      height: 'calc(320px + 2px) !important',
    },
  },
  variants: {
    animate: {
      true: {
        '& > *': {
          animation: `${slide} 10s infinite`,
          opacity: 0,
          '&:last-child': {
            animationDelay: '5s',
          },
        },
      },
    },
    stopAnimate: {
      true: {
        '& > *': {
          animationPlayState: 'paused',
        },
      },
    },
  },
});

const StyledContent = styled('div', {
  position: 'relative',
  width: '100%',
  '@lg': {
    width: 488,
    pe: 'none',
  },
  '& .usp-content': {
    paddingTop: '$space-6',
    '@lg': {
      paddingLeft: '$space-6',
    },
  },
  variants: {
    theme: {
      light: {
        [`& ${CardImage}`]: {
          borderColor: 'rgba(70, 70, 70, 0.8)',
          borderImage:
            'linear-gradient(to left, rgba(70, 70, 70, 0.24), rgba(70, 70, 70, 1)) 2',
          '@md': {
            borderImage:
              'linear-gradient(to left, rgba(70, 70, 70, 0.24), rgba(70, 70, 70, 1)) 3',
          },
          '&::before': {
            opacity: 0.7,
          },
        },
        [`& ${CardImageInner}`]: {
          bc: '$gray200',
        },
      },
      dark: {
        [`& ${CardImage}`]: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderImage:
            'linear-gradient(to left, rgba(70, 70, 70, 0.44), rgba(70, 70, 70, 1)) 2',
          '@md': {
            borderImage:
              'linear-gradient(to left, rgba(70, 70, 70, 0.44), rgba(70, 70, 70, 1)) 3',
          },
        },
        [`& ${CardImageInner}`]: {
          bc: '$black',
        },
      },
    },
  },
});

interface IUspContentProps extends React.ComponentProps<typeof StyledContent> {
  css?: CSS;
  className?: string;
  theme?: IMy24Theme;
  item?: IItem;
  dataTest?: string;
  onClick?: () => void;
  open?: boolean;
}

const ResponsiveImage = ({ src, alt, imageDesktopSrc }) => (
  <Image
    rootMargin="20% 0px 0px 0px"
    hoverEffect="zoom"
    src={src}
    alt={alt}
    width={600}
    height={400}
    layout="fill"
    sources={[
      {
        srcset: imageDesktopSrc,
        media: { minWidth: 1024 },
        options: { width: 640, crop: '16:9' },
      },
      ...generateTabletAndMobileImageSources({
        mobileSrc: src,
        mobileOptions: {
          crop: '3:2',
        },
      }),
    ]}
  />
);

export const UspContent: React.FC<IUspContentProps> = ({
  css,
  className,
  theme = 'light',
  item,
  dataTest = 'content',
  onClick,
  open,
}) => {
  return (
    <StyledContent
      css={css}
      className={className}
      theme={theme}
      data-test={dataTest}
    >
      {!!item?.image?.src && (
        <CardImage
          position="relative"
          width="full"
          onClick={onClick}
          className="usp-card-image"
          data-test="usp_content_card_image"
        >
          <CardImageInner
            position="relative"
            width="full"
            height="full"
            overflow="hidden"
            data-test="usp_content_image"
            animate={!!(item?.image2?.src && item?.imageDesktop2?.src)}
            stopAnimate={open}
          >
            <ResponsiveImage
              src={item.image.src}
              alt={item.image.alt}
              imageDesktopSrc={item.imageDesktop?.src}
            />
            {!!item.image2?.src && !!item.imageDesktop2?.src && (
              <ResponsiveImage
                src={item.image2.src}
                alt={item.image2.alt}
                imageDesktopSrc={item.imageDesktop2.src}
              />
            )}
          </CardImageInner>
        </CardImage>
      )}
      <Content
        {...item}
        className="usp-content"
        theme={theme}
        descriptionTheme={theme}
        css={{ pe: 'all' }}
      />
    </StyledContent>
  );
};

UspContent.displayName = 'UspContent';
